import React, { Component } from 'react'

import { inject, observer } from 'mobx-react'
import { IStore, IStory } from '../../../../store/models'
import { IStories } from '../../../../store/models/Stories'
import { withRouter } from 'react-router'
import { flow } from 'lodash'
import DataTable, { IColData, ValueLabel } from '../../../shared/DataTableFilterable'
import { Chip, Grid, IconButton, Tooltip, Link as MuiLink } from '@material-ui/core'
import { truncate } from 'lodash'

import moment from 'moment'

/**
 * The confirm text for declining a story. Exported so the test asserts the
 * words the moderator is actually shown, rather than that *something* was
 * confirmed.
 *
 * It names the story, because in a table of truncated cells the row you meant
 * to click and the row you did click look alike; and it says the two things
 * that cannot be taken back -- the submission is destroyed, and the person
 * who sent it hears nothing.
 */
export const declineConfirmText = (headline: string) =>
  `Delete this story permanently?\n\n"${headline}"\n\n` +
  'Declining destroys the submission. This cannot be undone, and the person who sent it is not notified.'

/**
 * Per-row controls.
 *
 * `pending` is the approve-before-publish queue rather than a different kind
 * of row: every story in it is simply one with `visible: false`. So Approve is
 * the SAME call the All tab's toggle makes -- `updateVisibility`, the one path
 * to `StoriesApi.update(id, { visible })` -- with a label and icon that say
 * "approve" where that is what the click means.
 *
 * Decline is not that. There is no "declined" column to write, so declining
 * DESTROYS the story, and the two actions are deliberately not equal in
 * weight: Approve is a plain icon, Decline is red and asks first. The confirm
 * follows the one destructive pattern this admin already has --
 * `partials/NpsActions.tsx` guards its delete with `window.confirm` -- rather
 * than introducing a second convention for the same gesture.
 *
 * ⛔ Decline does NOT replace the hide/show toggle. Hiding a published story
 * and destroying an unreviewed one are different acts; the toggle keeps its
 * place in the All tab, untouched.
 */
export const StoryActions = observer(
  ({ row, pending, onDecline }: { row: IStory; pending: boolean; onDecline(declined: IStory): void }) => {
    return (
      <>
        <Tooltip title='View Story'>
          <IconButton size='small' onClick={() => row.show()}>
            <i className='fas fa-search opacity-8'></i>
          </IconButton>
        </Tooltip>
        {pending ? (
          <>
            <Tooltip title='Approve — publish this story to the topic page'>
              <IconButton size='small' aria-label='Approve story' onClick={() => row.updateVisibility(true)}>
                <i className='fas fa-check opacity-8' />
              </IconButton>
            </Tooltip>
            <Tooltip title='Decline — delete this story permanently'>
              <IconButton
                size='small'
                aria-label='Decline story'
                style={{ color: '#C62828' }}
                onClick={(e) => {
                  e.stopPropagation()
                  e.preventDefault()
                  if ((window as FIXME).confirm(declineConfirmText(row.headline))) {
                    onDecline(row)
                  }
                }}
              >
                <i className='fal fa-trash-alt' />
              </IconButton>
            </Tooltip>
          </>
        ) : (
          <Tooltip title={row.visible ? 'Hide' : 'Show'}>
            {/*
              * `aria-label` added alongside the two new buttons. The toggle's
              * only child is an `<i>` whose glyph comes from a font-awesome
              * `::before`, so there was no text to announce and a screen
              * reader read it as an unnamed "button" -- the same gap that was
              * fixed on the story share controls. The behaviour is unchanged.
              */}
            <IconButton size='small' aria-label={row.visible ? 'Hide' : 'Show'} onClick={() => row.updateVisibility(!row.visible)}>
              {row.visible ? <i className='fas fa-eye-slash opacity-8' /> : <i className='fas fa-eye opacity-8' />}
            </IconButton>
          </Tooltip>
        )}
      </>
    )
  }
)

/**
 * Hidden rows are drawn as a chip rather than the word "Hidden", so a story
 * that is not on the public wall is visible as one while scanning the All
 * tab -- which is where an unapproved story would otherwise sit unnoticed
 * among hundreds of published ones.
 *
 * The wording stops at "Hidden" on purpose. `visible: false` is the only state
 * the record has: a story an admin hid by hand and a story that has never been
 * reviewed are the same row, and calling either one "pending" would be a
 * guess. See the note on the Awaiting approval tab in StoriesSummary.
 */
const ShowVisivility = observer(({ row }: { row: IStory }) =>
  row.visible ? (
    <ValueLabel value={<Chip size='small' label='Visible' />} />
  ) : (
    <ValueLabel
      value={
        <Tooltip title='Not on the public wall. Approve it to publish.'>
          <Chip size='small' label='Hidden' style={{ backgroundColor: '#F7BF14', fontWeight: 600 }} />
        </Tooltip>
      }
    />
  )
)

/**
 * How one cell of the moderation table is drawn.
 *
 * Lifted out of the component because `DataTableFilterable` renders through
 * `@devexpress/dx-react-grid`'s `VirtualTable`, which draws NOTHING under
 * jsdom -- not one row, not even the header. Measured, not assumed: a probe
 * that mounted the table with a story in the store found neither the author's
 * name nor the column titles anywhere in the document.
 *
 * So the cell logic is the seam that can actually be tested, and the defect
 * this file exists to fix -- an author showing as "-" -- lives exactly here.
 */
export const storyCellValue = (
  row: IStory,
  columnName: string,
  { pending, onDecline }: { pending: boolean; onDecline(declined: IStory): void }
) => {
  switch (columnName) {
    case 'title':
      return (
        <ValueLabel
          value={
            <MuiLink rel='noopener' title={row.title} href={row.topicLink} target='blank' color='primary'>
              {row.title}
            </MuiLink>
          }
        />
      )
    case 'actions':
      return (
        <Grid container direction='row' justify='space-around' alignItems='center'>
          <StoryActions row={row} pending={pending} onDecline={onDecline} />
        </Grid>
      )
    case 'startsAt':
      // The guard is load-bearing now that the field is nullable:
      // `moment(null).format('L')` is the string "Invalid date", which
      // would land in the cell looking like a date the admin should be
      // able to make sense of.
      return row.startsAt ? (
        <ValueLabel value={moment(row.startsAt).format('L')} />
      ) : (
        <ValueLabel value={undefined} title='No conversation — submitted against the topic' />
      )
    case 'createdAt':
      return <ValueLabel value={row.createdAt && moment(row.createdAt).format('L')} />
    case 'author':
      return <ValueLabel value={row.displayAuthorName} empty='-' />
    case 'authorEmail':
      // A mailto rather than plain text: the point of the column is
      // that a moderator can act on it without retyping an address
      // they were given by someone they have no other channel to.
      return row.authorEmail ? (
        <ValueLabel
          value={
            <MuiLink rel='noopener' title={row.authorEmail} href={`mailto:${row.authorEmail}`} color='primary'>
              {row.authorEmail}
            </MuiLink>
          }
        />
      ) : (
        <ValueLabel value={undefined} title='No email — this story was filed by a signed-in author' />
      )
    case 'visible':
      return <ShowVisivility row={row} />
    case 'prompt':
      return <ValueLabel value={truncate(row.prompt?.toString(), { length: 30, omission: '...' })} />
    case 'asset':
      return (
        <a href={`${row.asset?.url}`} target='blank'>
          <ValueLabel value={truncate(row.asset?.url?.toString(), { length: 45, omission: '...' })} />
        </a>
      )
    case 'headline':
      return <ValueLabel value={truncate(row.headline, { length: 35, omission: '...' })} />
    case 'content':
      return <ValueLabel value={truncate(row.content, { length: 45, omission: '...' })} />
    default:
      return <ValueLabel value={row[columnName]} />
  }
}

class StoriesTable extends Component<any, any> {
  render() {
    const { loading, filterable } = this.props
    const stories: IStories = this.props.stories
    // Read off the store inside render, not in `inject`: `scopedList` returns a
    // new plain array each time, so resolving it in the injector would hand the
    // observer a value it never subscribed to and the table would not redraw
    // when a story is approved.
    const pending = stories.visibleScope === 'pending'
    const items = stories.scopedList

    const columnData: IColData[] = [
      { name: 'id', title: 'ID', width: 80, sortable: true },
      { name: 'title', title: 'Conversation Topic', width: 250, sortable: true },
      /**
       * Kept as "Date of Conversation", and kept meaning exactly that.
       *
       * A topic-level story has no conversation, so this is blank for one --
       * `ValueLabel` draws its "-" placeholder. It deliberately does NOT fall
       * back to `createdAt`: the column two along is already "Date of
       * Submission", which is that value, and filling this one with it would
       * put the same date under two headings and imply a dinner that never
       * happened.
       */
      { name: 'startsAt', title: 'Date of Conversation', width: 150, sortable: true },
      { name: 'asset', title: 'Video or Image Link', width: 300 },
      { name: 'prompt', title: 'Prompt Text', width: 250, sortable: true },
      { name: 'visible', title: 'Visibility', width: 100, sortable: true },
      { name: 'headline', title: 'Headline', width: 250, sortable: true },
      { name: 'content', title: 'Response', width: 350, sortable: true },
      /**
       * One Author column, replacing the separate First Name / Last Name pair.
       *
       * Those two read `firstName`/`lastName`, which come off the USER record.
       * A QR-card submission has no user, so both were null and every card
       * story showed "-" for its author -- while the name the submitter
       * actually typed sat in `author_name`, served and unread. A moderator
       * deciding about a stranger could not see who the stranger was.
       *
       * `displayAuthorName` already resolves this correctly for both kinds of
       * story (account name, else typed name, else empty); the table simply
       * was not calling it. Two columns that are structurally empty for a
       * whole class of row are worse than one that is right for every row.
       *
       * ⚠️ NOT sortable, deliberately, and this is a real loss worth naming:
       * First Name and Last Name did sort, server-side and correctly
       * (`ListService` runs `sorted_id.underscore`, so `firstName` arrives as
       * `first_name` and `Admin::StoriesService` left-joins the user). No
       * single sort key covers both sources -- `first_name`/`last_name` order
       * the account and are NULL for card rows, `author_name` orders the typed
       * name and is NULL for account rows. Either would sort half the table
       * and silently lump the other half together, which is exactly the
       * "claims to sort and does not" failure. Restoring it needs the server
       * to order on a COALESCE of the two, keyed on a new sortable field.
       */
      { name: 'author', title: 'Author', width: 180 },
      /**
       * ⛔ Admin-only, and this component is the right place for it.
       * `Api::Admin::StoriesController#index` is the only endpoint that serves
       * `author_email` (it is on `Admin::StoriesSerializer`, never on
       * `StorySerializer`), and this table is only reachable behind the admin
       * routes. It must NOT be added to `StoryShowDialog` or `StoryAuthor` --
       * both of those render on the public topic page too.
       */
      { name: 'authorEmail', title: 'Author Email', width: 220 },
      { name: 'createdAt', title: 'Date of Submission', width: 150, sortable: true },
      { name: 'actions', title: 'Actions', align: 'center' }
    ]

    return (
      <DataTable
        height={'calc(100vh - 16em)'}
        data={items}
        columns={columnData}
        loading={loading}
        filterable={filterable}
        rightColumns={['actions']}
        hideSearch={true}
        noDataMsg={pending ? 'Nothing is waiting for approval.' : undefined}
        getCellValue={(row: IStory, columnName: string) => storyCellValue(row, columnName, { pending, onDecline: (story) => stories.deleteStory(story) })}
      />
    )
  }
}

export default flow(
  observer,
  inject<FIXME, FIXME>(
    ({
      mst: {
        auth: { user },
        stories
      }
    }: {
      mst: IStore
    }) => {
      return {
        user,
        stories,
        filterable: stories,
        loading: stories.loadingIds.includes('stories_table')
      }
    }
  ),
  withRouter
)(StoriesTable)
